/**
 * List and Segment Types
 *
 * Supports dynamic contact lists with advanced query builder functionality.
 * Lists can be dynamic (calculated from query criteria) or static (fixed membership).
 */

import type { UUID } from "./common.types";

// =====================================================
// CONTACT TYPES (Enhanced)
// =====================================================

export type ContactType =
  | "Member"
  | "Expired Member"
  | "Prospect"
  | "Organization Contact"
  | "User";

export type ContactStatus = "active" | "inactive" | "archived";

export type RelationshipType =
  | "spouse"
  | "parent"
  | "child"
  | "sibling"
  | "guardian"
  | "dependent"
  | "payer"
  | "beneficiary"
  | "other";

export interface ContactRelationship {
  id: UUID;
  contact_id: UUID;
  related_contact_id: UUID;
  relationship_type: RelationshipType;
  is_primary: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ContactStatusHistoryEntry {
  id: UUID;
  contact_id: UUID;
  old_status?: string;
  new_status: string;
  old_contact_type?: ContactType;
  new_contact_type?: ContactType;
  old_paying_member?: boolean;
  new_paying_member?: boolean;
  changed_by?: UUID;
  changed_at: string;
  reason?: string;
  notes?: string;
}

// =====================================================
// QUERY BUILDER TYPES
// =====================================================

export type QueryOperator = "AND" | "OR";

export type FieldOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "in"
  | "not_in"
  | "greater_than"
  | "less_than"
  | "greater_than_or_equal"
  | "less_than_or_equal"
  | "is_null"
  | "is_not_null"
  | "between"
  // Zuora Account Last Invoice Date (Phase 3): "In the previous N calendar days".
  // Calendar-date window in America/Chicago: last_invoice_date >= today-N AND < today.
  // Excludes today; N is a positive whole number. Compiled account-side against
  // zuora_accounts.last_invoice_date (does NOT change existing absolute-date criteria).
  | "previous_calendar_days"
  | "within_days"
  // Date relative-time operators (supported by build_strict_segment_sql
  // since Pardot phase 2A; added to the frontend operator list 2026-05-20
  // to match the SQL compiler).
  | "days_ago"
  | "days_in_future"
  // Proximity/radius operators (2026-07-02). value = [anchor, miles]:
  // anchor is a 5-digit ZIP, an organization id, a location id, or an event id.
  // Compiled by _segment_geo_predicate via build_strict_segment_sql (ST_DWithin).
  // within_radius_of_event (Slice 3, 2026-07-15): anchor = event id; the event's
  // geocoded coordinates (migration 009) are the center. Un-geocoded events raise
  // CRITERIA_NOT_EVALUABLE server-side.
  | "within_radius_of_zip"
  | "within_radius_of_org"
  | "within_radius_of_location"
  | "within_radius_of_event";

export type QueryField =
  // Custom contact attribute (NEW 2026-05-20): field syntax
  // 'custom:<slug>' references contact_attribute_values via the
  // segment compiler. Slug must be lowercase letters/digits/
  // underscores starting with a letter (enforced server-side).
  | `custom:${string}`
  // Core contact fields
  | "contact_type"
  | "paying_member"
  | "household_key"
  | "organization_id"
  | "location_id"
  | "tags"
  | "status"
  | "email"
  | "phone"
  | "first_name"
  | "last_name"
  | "created_at"
  | "updated_at"
  // Geographic fields
  | "state"
  | "city"
  | "postal_code"
  | "country"
  // Membership fields
  | "is_usls_member"
  | "member_status"
  | "member_expiration_date"
  | "member_renewal_date"
  | "member_start_date"
  | "member_end_date"
  | "member_date"
  | "last_invoice_date"
  // Abandoned-cart signal from the upstream checkout feed (2026-09-21). Accepted by
  // build_query_criteria_sql as of migration 2026-09-21-002 — these three MUST stay in step
  // with that function's per-field allowlist, or a segment saves and then fails to compile.
  | "cart_abandoned"
  | "furthest_step_reached"
  | "last_date_visited"
  // Zuora Account-level fields (Phase 3). No contacts column — BOTH the sequence
  // (build_query_criteria_sql) and strict-segment (build_strict_segment_sql) compilers
  // map these to a correlated EXISTS against public.zuora_accounts via
  // contacts.zuora_account_id (the canonical account row). Balance is account-level and
  // shared by every contact on the account — recipients are NOT deduplicated. Region/
  // Type/Status use canonical stored values (case-insensitive after trim).
  | "account_region"
  | "account_type"
  | "account_name"
  | "account_balance"
  | "account_status"
  // Communication preferences
  | "email_opt_in"
  | "sms_opt_in"
  // Subscription fields
  | "has_active_subscription"
  | "is_primary_member"
  // Virtual (EIB proximity #1): existing/former/cancelled member. No contacts column — the strict
  // segment compiler maps it to public.eib_is_current_or_former_member(alias.id).
  | "is_current_or_former_member"
  // Virtual (EIB #8 win-back): FORMER/CANCELLED member only (not active). Compiler maps it to
  // public.eib_is_former_or_cancelled_member(alias.id).
  | "is_former_or_cancelled_member"
  // Other fields
  | "source"
  | "product_tags"
  | "is_archived"
  // Email deliverability (suppression list — phase 1 of email suppression)
  | "is_email_suppressed"
  | "email_suppression_reason"
  // Virtual (NeverBounce email-quality): no contacts column — the strict segment
  // compiler maps it to public.contact_email_quality(alias.email), resolving to one
  // of valid / risky / invalid / unvalidated.
  | "email_quality"
  // Native Zuora entitlement targeting, matched against CURRENT ACTIVE subscriptions only.
  //   zuora_plan_name -> rate_plan_details[].productName (the coverage name, incl. "- Secondary")
  //   product_names   -> the broad product family
  | "zuora_plan_name"
  | "product_names"
  // Virtual (email-open engagement): no contacts column — the strict segment compiler
  // maps these to an EXISTS on public.contact_email_engagement (daily-refreshed).
  // Boolean Yes/No: did the contact open an email in the last 30 / 90 days.
  | "opened_email_30d"
  | "opened_email_90d"
  // Behavioral fields (event-based)
  | "event_performed"
  | "event_not_performed"
  // Segment membership
  | "segment_membership"
  // Proximity/radius (2026-07-02): a single virtual field whose operator
  // selects the anchor kind (ZIP / organization / location).
  | "proximity";

/**
 * Query condition value type
 * Represents all possible value types for the query builder:
 * - string: text fields, UUIDs, dates (ISO format)
 * - number: numeric comparisons
 * - boolean: true/false fields like paying_member
 * - string[]: array values for 'in', 'not_in', and 'tags' operators
 * - [number, number] | [string, string]: range values for 'between' operator
 * - null: for 'is_null', 'is_not_null' operators
 */
export type QueryConditionValue =
  | string
  | number
  | boolean
  | string[]
  | [number, number]
  | [string, string]
  | [string, number] // proximity: [anchor, radiusMiles]
  | null;

export interface QueryCondition {
  field: QueryField;
  operator: FieldOperator;
  value: QueryConditionValue;
}

export interface QueryGroup {
  operator: QueryOperator;
  conditions: (QueryCondition | QueryGroup)[];
}

// Root query criteria type
export type QueryCriteria = QueryGroup;

// =====================================================
// TRIGGER TYPES (Phase 3)
// =====================================================

/**
 * Types of automatic triggers for dynamic list voucher generation
 */
export type ListTriggerType =
  | "NONE" // No automatic trigger (manual or query-only)
  | "ANNIVERSARY" // X months/years since member_date/member_start_date
  | "RENEWAL_DUE" // X days before member_expiration_date
  | "INACTIVITY" // No activity in X days
  | "MILESTONE" // Reached spending/engagement threshold
  | "BIRTHDAY" // Contact's birthday
  | "CUSTOM"; // Custom date field based

/**
 * Configuration for different trigger types
 */
export interface TriggerConfigAnniversary {
  months: number; // Number of months since member start (12 = 1 year)
  date_field?: "member_start_date" | "member_date" | "created_at";
}

export interface TriggerConfigRenewalDue {
  days_before: number; // Days before expiration to trigger
}

export interface TriggerConfigInactivity {
  days: number; // Days of inactivity
}

export interface TriggerConfigMilestone {
  field: string; // Field to check
  threshold: number; // Value threshold
}

export interface TriggerConfigBirthday {
  days_before?: number; // Days before birthday (0 = on birthday)
}

export interface TriggerConfigCustom {
  date_field: string; // Custom date field name
  days_offset?: number; // Days offset from date (negative = before, positive = after)
}

export type TriggerConfig =
  | TriggerConfigAnniversary
  | TriggerConfigRenewalDue
  | TriggerConfigInactivity
  | TriggerConfigMilestone
  | TriggerConfigBirthday
  | TriggerConfigCustom
  | Record<string, unknown>;

// =====================================================
// LIST TYPES
// =====================================================

export interface List {
  id: UUID;
  name: string;
  description?: string;
  organization_id?: UUID;
  query_criteria: QueryCriteria;
  is_dynamic: boolean;
  contact_count: number;
  last_calculated?: string;
  custom_attributes?: Record<string, string>; // For campaign URL parameter mapping

  // Combined static list (A1 multi-segment combine). list_type='combined_snapshot' marks a
  // materialized union/difference of other segments; build_status tracks the async drain
  // ('building' | 'ready' | 'failed'; NULL for normal lists). metadata holds source provenance.
  list_type?: string | null;
  build_status?: "building" | "ready" | "failed" | null;
  metadata?: Record<string, unknown> | null;
  created_by?: UUID;
  updated_by?: UUID;
  created_at: string;
  updated_at: string;

  // Trigger configuration (Phase 3)
  trigger_type?: ListTriggerType;
  trigger_config?: TriggerConfig;
  auto_generate_vouchers?: boolean;
  linked_offer_id?: UUID;
  evaluation_interval_minutes?: number;
  last_evaluated_at?: string;
  next_evaluation_at?: string;
  is_active?: boolean;

  // Segment archive (migration 1470). archived_at set = hidden from pickers and refused by flight scheduling.
  // archive_state: 'archived' (members still here) | 'offloading' | 'offloaded' (members in the archive file)
  // | 'restoring' (unarchive in progress).
  archived_at?: string | null;
  archive_state?: "archived" | "offloading" | "offloaded" | "restoring" | null;
  archive_member_rows?: number | null;
}

export interface ListContact {
  id: UUID;
  list_id: UUID;
  contact_id: UUID;
  added_by?: UUID;
  added_at: string;
}

export interface EventList {
  id: UUID;
  event_id: UUID;
  list_id: UUID;
  created_at: string;
}

/**
 * Tracks vouchers issued to contacts via trigger-based lists
 * Prevents duplicate issuance for the same list/contact/offer combo
 */
export interface ListVoucherIssuance {
  id: UUID;
  list_id: UUID;
  contact_id: UUID;
  offer_id: UUID;
  voucher_id?: UUID;
  issued_at: string;
  trigger_type: ListTriggerType;
  trigger_data?: Record<string, unknown>;
}

/**
 * List with trigger info and linked offer details
 */
export interface ListWithTriggerInfo extends List {
  linked_offer_name?: string;
  linked_offer_code?: string;
  linked_offer_value?: number;
  total_vouchers_issued?: number;
}

// =====================================================
// CREATE/UPDATE TYPES
// =====================================================

export interface CreateListInput {
  name: string;
  description?: string;
  organization_id?: UUID;
  query_criteria: QueryCriteria;
  is_dynamic: boolean;
  custom_attributes?: Record<string, string>;
  // Trigger configuration (Phase 3)
  trigger_type?: ListTriggerType;
  trigger_config?: TriggerConfig;
  auto_generate_vouchers?: boolean;
  linked_offer_id?: UUID;
  evaluation_interval_minutes?: number;
}

export interface UpdateListInput {
  name?: string;
  description?: string;
  organization_id?: UUID;
  query_criteria?: QueryCriteria;
  is_dynamic?: boolean;
  custom_attributes?: Record<string, string>;
  // Trigger configuration (Phase 3)
  trigger_type?: ListTriggerType;
  trigger_config?: TriggerConfig;
  auto_generate_vouchers?: boolean;
  linked_offer_id?: UUID;
  evaluation_interval_minutes?: number;
  is_active?: boolean;
}

export interface AddContactsToListInput {
  list_id: UUID;
  contact_ids: UUID[];
}

export interface RemoveContactsFromListInput {
  list_id: UUID;
  contact_ids: UUID[];
}

// =====================================================
// QUERY BUILDER UI TYPES
// =====================================================

export interface QueryBuilderState {
  criteria: QueryCriteria;
  isValid: boolean;
  errors: string[];
}

export interface FieldOption {
  value: QueryField;
  label: string;
  type:
    | "text"
    | "number"
    | "boolean"
    | "date"
    | "array"
    | "uuid"
    | "select"
    | "proximity"
    // Zuora entitlement multi-select, sourced live from active subscriptions.
    | "zuora";
  // Optional inline help rendered under the condition row (e.g. the account-level
  // "shared balance / add Is Primary Member = Yes" guidance for Zuora Account fields).
  help?: string;
}

export interface OperatorOption {
  value: FieldOperator;
  label: string;
  compatibleTypes: Array<
    "text" | "number" | "boolean" | "date" | "array" | "uuid" | "proximity"
  >;
}

// =====================================================
// CONTACT PREVIEW TYPES
// =====================================================

export interface ListContactPreview {
  id: UUID;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  contact_type: ContactType;
  contact_types?: string[];
  paying_member: boolean;
  has_active_subscription?: boolean;
  organization_name?: string;
  location_name?: string;
  tags?: string[];
}

export interface ListWithContacts extends List {
  contacts?: ListContactPreview[];
  preview_count?: number;
}

// =====================================================
// ANALYTICS TYPES
// =====================================================

export interface ListAnalytics {
  total_lists: number;
  total_dynamic_lists: number;
  total_static_lists: number;
  total_contacts_in_lists: number;
  most_used_lists: Array<{
    list_id: UUID;
    list_name: string;
    usage_count: number;
  }>;
  contact_type_breakdown: {
    members: number;
    expired_members: number;
    prospects: number;
  };
  paying_member_breakdown: {
    paying: number;
    non_paying: number;
  };
}

// =====================================================
// VALIDATION HELPERS
// =====================================================

export const FIELD_OPTIONS: FieldOption[] = [
  // Core contact fields
  { value: "contact_type", label: "Contact Type", type: "text" },
  { value: "paying_member", label: "Paying Member", type: "boolean" },
  { value: "household_key", label: "Household Key", type: "text" },
  { value: "organization_id", label: "Organization", type: "uuid" },
  { value: "location_id", label: "Location", type: "uuid" },
  { value: "tags", label: "Tags", type: "array" },
  { value: "status", label: "Status", type: "text" },
  { value: "email", label: "Email", type: "text" },
  { value: "phone", label: "Phone", type: "text" },
  { value: "first_name", label: "First Name", type: "text" },
  { value: "last_name", label: "Last Name", type: "text" },
  { value: "created_at", label: "Created Date", type: "date" },
  { value: "updated_at", label: "Updated Date", type: "date" },
  // Geographic fields
  { value: "state", label: "State", type: "text" },
  { value: "city", label: "City", type: "text" },
  { value: "postal_code", label: "Postal Code", type: "text" },
  { value: "country", label: "Country", type: "text" },
  // Membership fields
  { value: "is_usls_member", label: "Is USLS Member", type: "boolean" },
  { value: "member_status", label: "Member Status", type: "text" },
  {
    value: "member_expiration_date",
    label: "Member Expiration Date",
    type: "date",
  },
  { value: "member_renewal_date", label: "Member Renewal Date", type: "date" },
  { value: "member_start_date", label: "Member Start Date", type: "date" },
  { value: "member_end_date", label: "Member End Date", type: "date" },
  { value: "member_date", label: "Member Date", type: "date" },
  {
    value: "last_invoice_date",
    label: "Zuora Account Last Invoice Date",
    type: "date",
    help:
      'Only the "In the previous N calendar days" operator reads the canonical Zuora account ' +
      "(a calendar-date window in America/Chicago that excludes today). The other operators " +
      "(equals, before/after, between, within-last-N-days) evaluate the per-contact mirror " +
      "column and are unchanged — the two can differ if a contact's mirror is momentarily " +
      "behind the account.",
  },
  // Zuora Account-level fields (Phase 3). Account-level values shared by every contact on
  // the account; compiled via a correlated EXISTS against zuora_accounts. See help text.
  {
    value: "account_region",
    label: "Zuora Account Region",
    type: "select",
    help:
      "Account-level Zuora region (canonical full names). An account-level condition can " +
      "match multiple contacts on one account. Add Is Primary Member = Yes to target one " +
      "recipient per account.",
  },
  {
    value: "account_type",
    label: "Zuora Account Type",
    type: "select",
    help:
      "Account-level Zuora type. An account-level condition can match multiple contacts on " +
      "one account. Add Is Primary Member = Yes to target one recipient per account.",
  },
  {
    value: "account_name",
    label: "Zuora Account Name",
    type: "text",
    help:
      'Account-level Zuora account name. "Does not equal" is case-insensitive after trimming; ' +
      "a missing/blank name is excluded (fails closed).",
  },
  {
    value: "account_balance",
    label: "Zuora Account Balance",
    type: "number",
    help:
      "Account-level Zuora balance. Every contact attached to the same Zuora account shares " +
      "this value. Add Is Primary Member = Yes to target one recipient per account.",
  },
  {
    value: "account_status",
    label: "Zuora Account Status",
    type: "select",
    help:
      "Account-level Zuora status (Active/Canceled). An account-level condition can match " +
      "multiple contacts on one account. Add Is Primary Member = Yes to target one recipient " +
      "per account.",
  },
  // Communication preferences
  { value: "email_opt_in", label: "Email Opt-In", type: "boolean" },
  { value: "sms_opt_in", label: "SMS Opt-In", type: "boolean" },
  // Subscription fields
  {
    value: "has_active_subscription",
    label: "Has Active Subscription",
    type: "boolean",
  },
  { value: "is_primary_member", label: "Is Primary Member", type: "boolean" },
  // Checkout progress (2026-09-21). Written by the prospect-create API. Until now these were
  // visible only on the Contact Detail "Checkout Progress" panel — one contact at a time — so
  // the population could not be assembled at all. The matching names had to be added to
  // build_query_criteria_sql's allowlist (migration 2026-09-21-002); that function FAILS
  // CLOSED on an unknown field, so offering them here without it would have thrown the moment
  // a list or sequence evaluated the condition.
  { value: "cart_abandoned", label: "Cart Abandoned", type: "boolean" },
  {
    value: "furthest_step_reached",
    label: "Furthest Checkout Step",
    type: "text",
  },
  { value: "last_date_visited", label: "Last Date Visited", type: "date" },
  // Other fields
  { value: "source", label: "Source", type: "text" },
  { value: "product_tags", label: "Product Tags", type: "array" },
  { value: "is_archived", label: "Is Archived", type: "boolean" },
  // Email deliverability (suppression list)
  {
    value: "is_email_suppressed",
    label: "Email Suppressed",
    type: "boolean",
  },
  {
    value: "email_suppression_reason",
    label: "Email Suppression Reason",
    type: "select",
  },
  // NeverBounce email quality (virtual field). 'invalid' is auto-excluded from
  // campaign sends regardless of this filter; use this to target/exclude the
  // other buckets (e.g. only 'valid', or drop 'risky').
  {
    value: "email_quality",
    label: "Email quality (NeverBounce)",
    type: "select",
  },
  // Zuora entitlements. Only a CURRENT ACTIVE subscription can match: a cancelled member never
  // matches on a plan they used to hold.
  {
    value: "zuora_plan_name",
    label: "Zuora Plan (coverage)",
    type: "zuora",
  },
  {
    value: "product_names",
    label: "Zuora Product Family",
    type: "zuora",
  },
  // Email-open engagement (virtual, daily-refreshed contact_email_engagement).
  // Boolean Yes/No: opened an email within the last 30 / 90 days. equals/not_equals only.
  {
    value: "opened_email_30d",
    label: "Opened email (last 30 days)",
    type: "boolean",
  },
  {
    value: "opened_email_90d",
    label: "Opened email (last 90 days)",
    type: "boolean",
  },
  // Behavioral fields (event-based)
  { value: "event_performed", label: "Has Done (Event)", type: "text" },
  { value: "event_not_performed", label: "Has Not Done (Event)", type: "text" },
  // Segment membership
  { value: "segment_membership", label: "Segment Membership", type: "uuid" },
  // Proximity/radius — operator picks the anchor kind (ZIP / org / location)
  { value: "proximity", label: "Location (within radius)", type: "proximity" },
];

export const OPERATOR_OPTIONS: OperatorOption[] = [
  {
    value: "equals",
    label: "Equals",
    compatibleTypes: ["text", "number", "boolean", "uuid", "date"],
  },
  {
    value: "not_equals",
    label: "Not Equals",
    compatibleTypes: ["text", "number", "boolean", "uuid", "date"],
  },
  { value: "contains", label: "Contains", compatibleTypes: ["text", "array"] },
  {
    value: "not_contains",
    label: "Does Not Contain",
    compatibleTypes: ["text", "array"],
  },
  { value: "starts_with", label: "Starts With", compatibleTypes: ["text"] },
  { value: "ends_with", label: "Ends With", compatibleTypes: ["text"] },
  { value: "in", label: "In", compatibleTypes: ["text", "number", "uuid"] },
  {
    value: "not_in",
    label: "Not In",
    compatibleTypes: ["text", "number", "uuid"],
  },
  {
    value: "greater_than",
    label: "Greater Than",
    compatibleTypes: ["number", "date"],
  },
  {
    value: "less_than",
    label: "Less Than",
    compatibleTypes: ["number", "date"],
  },
  {
    value: "greater_than_or_equal",
    label: "Greater Than or Equal",
    compatibleTypes: ["number", "date"],
  },
  {
    value: "less_than_or_equal",
    label: "Less Than or Equal",
    compatibleTypes: ["number", "date"],
  },
  {
    value: "is_null",
    label: "Is Empty",
    compatibleTypes: ["text", "number", "boolean", "uuid", "date", "array"],
  },
  {
    value: "is_not_null",
    label: "Is Not Empty",
    compatibleTypes: ["text", "number", "boolean", "uuid", "date", "array"],
  },
  { value: "between", label: "Between", compatibleTypes: ["number", "date"] },
  // Zuora Account Last Invoice Date only (Phase 3). Calendar-date window in
  // America/Chicago: >= today-N AND < today. Excludes today. N = positive whole number.
  {
    value: "previous_calendar_days",
    label: "In the previous N calendar days",
    compatibleTypes: ["date"],
  },
  // Date relative-time operators — SQL compiler accepts these on
  // datetime built-ins (created_at, member_renewal_date, etc.) and on
  // custom:<slug> date attributes. Added to the UI 2026-05-20.
  {
    value: "days_ago",
    label: "Within Last N Days",
    compatibleTypes: ["date"],
  },
  {
    value: "days_in_future",
    label: "In Next N Days",
    compatibleTypes: ["date"],
  },
  {
    value: "within_days",
    label: "Within Last X Days",
    compatibleTypes: ["text"],
  },
  // Proximity/radius operators — only valid on the 'proximity' field.
  {
    value: "within_radius_of_zip",
    label: "Within radius of ZIP",
    compatibleTypes: ["proximity"],
  },
  {
    value: "within_radius_of_org",
    label: "Within radius of organization",
    compatibleTypes: ["proximity"],
  },
  {
    value: "within_radius_of_location",
    label: "Within radius of location",
    compatibleTypes: ["proximity"],
  },
  {
    value: "within_radius_of_event",
    label: "Within radius of event",
    compatibleTypes: ["proximity"],
  },
];

export const CONTACT_TYPE_OPTIONS: Array<{
  value: ContactType;
  label: string;
}> = [
  { value: "Member", label: "Member" },
  { value: "Expired Member", label: "Expired Member" },
  { value: "Prospect", label: "Prospect" },
  { value: "Organization Contact", label: "Organization Contact" },
  { value: "User", label: "User" },
];

// US States for state field dropdown
export const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
  "District of Columbia",
] as const;

// State abbreviations for alternate matching
export const US_STATE_ABBREVIATIONS: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

// Code/name pairs for select inputs. Display the full name to the
// user; persist the 2-letter code (matches what events.location_state
// stores in the DB). Used by the Events list + Reports filter bars.
export const US_STATE_OPTIONS: Array<{ code: string; name: string }> =
  Object.entries(US_STATE_ABBREVIATIONS)
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

// Email-quality (NeverBounce) options. Only these FOUR buckets are exposed —
// raw sub-codes (catchall/unknown/disposable) are intentionally hidden. The
// labels carry the explanation inline so the choice is obvious in the dropdown.
export const EMAIL_QUALITY_OPTIONS = [
  { value: "valid", label: "Valid — confirmed deliverable" },
  {
    value: "risky",
    label: "Risky — unconfirmed (catch-all or no server response)",
  },
  {
    value: "invalid",
    label: "Invalid — undeliverable (auto-excluded from campaigns)",
  },
  { value: "unvalidated", label: "Not yet validated — never checked" },
] as const;

// Zuora Account canonical option lists (Phase 3). Values are the EXACT canonical
// stored strings — the UI must emit these so account criteria compare against the
// real account values (e.g. shorthand "Colorado" must never stand in for the full
// "U.S. Law Shield of Colorado"). Region is the FULL distinct set of stored
// zuora_accounts.region values (derived read-only from PROD 2026-08-26): the state
// regions plus LEO/Security/D.C./educational/NFA-code/business-entity variants. Used
// as an in/not_in multi-select. Re-derive from PROD if regions change.
export const ZUORA_ACCOUNT_REGION_OPTIONS = [
  { value: "CONFA", label: "CONFA" },
  { value: "FLNFA", label: "FLNFA" },
  { value: "MNFA", label: "MNFA" },
  { value: "OKNFA", label: "OKNFA" },
  { value: "PANFA", label: "PANFA" },
  { value: "TXNFA", label: "TXNFA" },
  { value: "Texas Law Shield", label: "Texas Law Shield" },
  { value: "Texas Law Shield LEO", label: "Texas Law Shield LEO" },
  { value: "Texas Law Shield Security", label: "Texas Law Shield Security" },
  { value: "U.S. Law Shield of Alabama", label: "U.S. Law Shield of Alabama" },
  { value: "U.S. Law Shield of Arizona", label: "U.S. Law Shield of Arizona" },
  {
    value: "U.S. Law Shield of Arkansas",
    label: "U.S. Law Shield of Arkansas",
  },
  {
    value: "U.S. Law Shield of California",
    label: "U.S. Law Shield of California",
  },
  {
    value: "U.S. Law Shield of Colorado",
    label: "U.S. Law Shield of Colorado",
  },
  {
    value: "U.S. Law Shield of Connecticut",
    label: "U.S. Law Shield of Connecticut",
  },
  {
    value: "U.S. Law Shield of Delaware",
    label: "U.S. Law Shield of Delaware",
  },
  { value: "U.S. Law Shield of Florida", label: "U.S. Law Shield of Florida" },
  {
    value: "U.S. Law Shield of Florida LEO",
    label: "U.S. Law Shield of Florida LEO",
  },
  { value: "U.S. Law Shield of Georgia", label: "U.S. Law Shield of Georgia" },
  { value: "U.S. Law Shield of Hawaii", label: "U.S. Law Shield of Hawaii" },
  { value: "U.S. Law Shield of Idaho", label: "U.S. Law Shield of Idaho" },
  {
    value: "U.S. Law Shield of Illinois",
    label: "U.S. Law Shield of Illinois",
  },
  { value: "U.S. Law Shield of Indiana", label: "U.S. Law Shield of Indiana" },
  { value: "U.S. Law Shield of Iowa", label: "U.S. Law Shield of Iowa" },
  { value: "U.S. Law Shield of Kansas", label: "U.S. Law Shield of Kansas" },
  {
    value: "U.S. Law Shield of Kentucky",
    label: "U.S. Law Shield of Kentucky",
  },
  {
    value: "U.S. Law Shield of Louisiana",
    label: "U.S. Law Shield of Louisiana",
  },
  {
    value: "U.S. Law Shield of Maryland",
    label: "U.S. Law Shield of Maryland",
  },
  {
    value: "U.S. Law Shield of Massachusetts",
    label: "U.S. Law Shield of Massachusetts",
  },
  {
    value: "U.S. Law Shield of Michigan",
    label: "U.S. Law Shield of Michigan",
  },
  {
    value: "U.S. Law Shield of Minnesota",
    label: "U.S. Law Shield of Minnesota",
  },
  {
    value: "U.S. Law Shield of Mississippi",
    label: "U.S. Law Shield of Mississippi",
  },
  {
    value: "U.S. Law Shield of Missouri",
    label: "U.S. Law Shield of Missouri",
  },
  { value: "U.S. Law Shield of Montana", label: "U.S. Law Shield of Montana" },
  {
    value: "U.S. Law Shield of Nebraska",
    label: "U.S. Law Shield of Nebraska",
  },
  { value: "U.S. Law Shield of Nevada", label: "U.S. Law Shield of Nevada" },
  {
    value: "U.S. Law Shield of New Hampshire",
    label: "U.S. Law Shield of New Hampshire",
  },
  {
    value: "U.S. Law Shield of New Jersey",
    label: "U.S. Law Shield of New Jersey",
  },
  {
    value: "U.S. Law Shield of New Mexico",
    label: "U.S. Law Shield of New Mexico",
  },
  {
    value: "U.S. Law Shield of North Carolina",
    label: "U.S. Law Shield of North Carolina",
  },
  {
    value: "U.S. Law Shield of North Dakota",
    label: "U.S. Law Shield of North Dakota",
  },
  { value: "U.S. Law Shield of Ohio", label: "U.S. Law Shield of Ohio" },
  {
    value: "U.S. Law Shield of Oklahoma",
    label: "U.S. Law Shield of Oklahoma",
  },
  { value: "U.S. Law Shield of Oregon", label: "U.S. Law Shield of Oregon" },
  {
    value: "U.S. Law Shield of Pennsylvania",
    label: "U.S. Law Shield of Pennsylvania",
  },
  {
    value: "U.S. Law Shield of Pennsylvania LEO",
    label: "U.S. Law Shield of Pennsylvania LEO",
  },
  {
    value: "U.S. Law Shield of South Carolina",
    label: "U.S. Law Shield of South Carolina",
  },
  {
    value: "U.S. Law Shield of South Dakota",
    label: "U.S. Law Shield of South Dakota",
  },
  {
    value: "U.S. Law Shield of Tennessee",
    label: "U.S. Law Shield of Tennessee",
  },
  { value: "U.S. Law Shield of Utah", label: "U.S. Law Shield of Utah" },
  { value: "U.S. Law Shield of Vermont", label: "U.S. Law Shield of Vermont" },
  {
    value: "U.S. Law Shield of Virginia",
    label: "U.S. Law Shield of Virginia",
  },
  {
    value: "U.S. Law Shield of Washington",
    label: "U.S. Law Shield of Washington",
  },
  {
    value: "U.S. Law Shield of Washington, D.C.",
    label: "U.S. Law Shield of Washington, D.C.",
  },
  {
    value: "U.S. Law Shield of West Virginia",
    label: "U.S. Law Shield of West Virginia",
  },
  {
    value: "U.S. Law Shield of Wisconsin",
    label: "U.S. Law Shield of Wisconsin",
  },
  { value: "U.S. Law Shield of Wyoming", label: "U.S. Law Shield of Wyoming" },
  {
    value: "U.S. LawShield Educational Institution",
    label: "U.S. LawShield Educational Institution",
  },
  { value: "Walker Rice PC", label: "Walker Rice PC" },
] as const;

export const ZUORA_ACCOUNT_TYPE_OPTIONS = [
  { value: "Standard", label: "Standard" },
  { value: "Comp", label: "Comp" },
  { value: "Facility", label: "Facility" },
  { value: "Employee", label: "Employee" },
] as const;

export const ZUORA_ACCOUNT_STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Canceled", label: "Canceled" },
] as const;

// Member status options
export const MEMBER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
  { value: "pending", label: "Pending" },
] as const;

export const CONTACT_STATUS_OPTIONS: Array<{
  value: ContactStatus;
  label: string;
}> = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];

export const RELATIONSHIP_TYPE_OPTIONS: Array<{
  value: RelationshipType;
  label: string;
}> = [
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "guardian", label: "Guardian" },
  { value: "dependent", label: "Dependent" },
  { value: "payer", label: "Payer" },
  { value: "beneficiary", label: "Beneficiary" },
  { value: "other", label: "Other" },
];

// Trigger type options for UI (Phase 3)
export const TRIGGER_TYPE_OPTIONS: Array<{
  value: ListTriggerType;
  label: string;
  description: string;
}> = [
  { value: "NONE", label: "None", description: "No automatic trigger" },
  {
    value: "ANNIVERSARY",
    label: "Anniversary",
    description: "X months/years since membership started",
  },
  {
    value: "RENEWAL_DUE",
    label: "Renewal Due",
    description: "X days before membership expires",
  },
  {
    value: "INACTIVITY",
    label: "Inactivity",
    description: "No activity in X days",
  },
  {
    value: "MILESTONE",
    label: "Milestone",
    description: "Reached a spending/engagement threshold",
  },
  {
    value: "BIRTHDAY",
    label: "Birthday",
    description: "Contact's birthday",
  },
  {
    value: "CUSTOM",
    label: "Custom Date",
    description: "Based on a custom date field",
  },
];

// Evaluation interval options for UI
export const EVALUATION_INTERVAL_OPTIONS = [
  { value: 60, label: "Every hour" },
  { value: 360, label: "Every 6 hours" },
  { value: 720, label: "Every 12 hours" },
  { value: 1440, label: "Daily" },
  { value: 10080, label: "Weekly" },
] as const;

// Field metadata for QueryBuilder UI - defines which fields have predefined options
export interface FieldMetadata {
  type:
    | "text"
    | "number"
    | "boolean"
    | "date"
    | "select"
    | "multi-select"
    | "uuid"
    | "array"
    | "proximity"
    | "zuora";
  options?: readonly { value: string; label: string }[] | readonly string[];
  placeholder?: string;
}

export const FIELD_METADATA: Partial<Record<QueryField, FieldMetadata>> = {
  contact_type: {
    type: "multi-select",
    options: CONTACT_TYPE_OPTIONS,
  },
  status: {
    type: "select",
    options: CONTACT_STATUS_OPTIONS,
  },
  state: {
    type: "select",
    options: US_STATES.map((s) => ({ value: s, label: s })),
    placeholder: "Select state...",
  },
  member_status: {
    type: "select",
    options: MEMBER_STATUS_OPTIONS,
  },
  paying_member: { type: "boolean" },
  is_usls_member: { type: "boolean" },
  email_opt_in: { type: "boolean" },
  sms_opt_in: { type: "boolean" },
  is_archived: { type: "boolean" },
  has_active_subscription: { type: "boolean" },
  is_primary_member: { type: "boolean" },
  // Email-open engagement virtual booleans (daily-refreshed contact_email_engagement).
  opened_email_30d: { type: "boolean" },
  opened_email_90d: { type: "boolean" },
  member_expiration_date: { type: "date" },
  member_renewal_date: { type: "date" },
  member_start_date: { type: "date" },
  member_end_date: { type: "date" },
  member_date: { type: "date" },
  last_invoice_date: { type: "date" },
  // Zuora Account-level fields (Phase 3). Region/Type/Status use canonical option lists;
  // Balance is numeric; Name is free text.
  account_region: {
    type: "multi-select",
    options: ZUORA_ACCOUNT_REGION_OPTIONS,
    placeholder: "Select region(s)...",
  },
  account_type: {
    type: "select",
    options: ZUORA_ACCOUNT_TYPE_OPTIONS,
    placeholder: "Select account type...",
  },
  account_status: {
    type: "select",
    options: ZUORA_ACCOUNT_STATUS_OPTIONS,
    placeholder: "Select status...",
  },
  account_balance: { type: "number" },
  account_name: { type: "text" },
  created_at: { type: "date" },
  updated_at: { type: "date" },
  organization_id: { type: "uuid", placeholder: "Select organization..." },
  location_id: { type: "uuid", placeholder: "Select location..." },
  proximity: { type: "proximity", placeholder: "Set a center and radius..." },
  segment_membership: { type: "select", placeholder: "Select segment..." },
  tags: { type: "array", placeholder: "Enter tags..." },
  product_tags: { type: "array", placeholder: "Enter product tags..." },
  // Behavioral fields use "select" type to show event type dropdown
  event_performed: { type: "select", options: [] }, // Options provided by CONTACT_EVENT_TYPE_OPTIONS
  event_not_performed: { type: "select", options: [] },
  // Email suppression — denormalized from email_suppressions table.
  // Reason vocabulary matches the source-of-truth CHECK constraint.
  is_email_suppressed: { type: "boolean" },
  email_suppression_reason: {
    type: "select",
    options: [
      { value: "unsubscribed", label: "Unsubscribed" },
      { value: "hard_bounced", label: "Hard Bounced" },
      {
        value: "soft_bounce_threshold",
        label: "Soft Bounce (threshold reached)",
      },
      { value: "spam_reported", label: "Spam Reported" },
      { value: "manual", label: "Manual" },
    ],
    placeholder: "Select reason...",
  },
  // NeverBounce email quality — virtual field compiled to
  // public.contact_email_quality(alias.email). Only equals/not_equals/in/not_in
  // are valid (constrained in getOperatorsForFieldType); is_null/is_not_null are
  // meaningless because the value is always one of the 4 buckets.
  email_quality: {
    type: "select",
    options: EMAIL_QUALITY_OPTIONS,
    placeholder: "Select email quality...",
  },
  // Zuora entitlements. Options are NOT static: the builder loads them live from
  // zuora_entitlement_values so the list always reflects current active subscriptions.
  // getFieldType reads this map, so these entries are what route the field to the
  // multi-select rather than the default free-text chip input.
  zuora_plan_name: {
    type: "zuora",
    placeholder: "Select current plan(s)...",
  },
  product_names: {
    type: "zuora",
    placeholder: "Select current product family...",
  },
};

// Contact event types for behavioral segment criteria
export const CONTACT_EVENT_TYPE_OPTIONS = [
  // Email events
  { value: "email_sent", label: "Email Sent" },
  { value: "email_delivered", label: "Email Delivered" },
  { value: "email_opened", label: "Email Opened" },
  { value: "email_clicked", label: "Email Clicked" },
  { value: "email_bounced", label: "Email Bounced" },
  // Voucher events
  { value: "voucher_generated", label: "Voucher Generated" },
  { value: "voucher_redeemed", label: "Voucher Redeemed" },
  { value: "voucher_cancelled", label: "Voucher Cancelled" },
  // External events
  { value: "offer_redeemed_external", label: "Offer Redeemed (External)" },
  { value: "offer_viewed_external", label: "Offer Viewed (External)" },
  // Member events
  { value: "member_signup", label: "Member Signup" },
  { value: "member_renewed", label: "Member Renewed" },
  { value: "member_expired", label: "Member Expired" },
  { value: "member_cancelled", label: "Member Cancelled" },
] as const;
